import * as Sentry from '@sentry/react';
import { useAtomValue } from 'jotai';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
  type Country,
} from 'react-phone-number-input';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import RestaurantName, { RestaurantNameLoader } from '../RestaurantName/RestaurantName.tsx';
import { isEventInfoReady, isValidEmail } from '../lib/ready.ts';
import {
  AuthAtom,
  UserAtom,
  useWidgetState,
  useRestaurantId,
  RestaurantAtom,
  resolveWidgetMarketingSmsOptIn,
} from '../lib/state.ts';
import { acceptTerms, subscribeToGeneralMarketingSms, verify } from '@/lib/api.ts';
import { recaptchaTokenFor } from '@/lib/recaptcha.ts';
import { track, resolveBookingType } from '@/lib/analytics/index.ts';
import type { WidgetPhoneRejectReason } from '@/lib/analytics/events.ts';
import { getValidPhone } from '@/lib/getPhone.ts';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { InputField } from '@dorsia/web-design-system/src/atoms/InputField';
import { Button } from '@dorsia/web-design-system/src/atoms/Button';
import { Badge } from '@dorsia/web-design-system/src/atoms/Badge';
import { Checkbox } from '@dorsia/web-design-system/src/atoms/Checkbox';
import { SectionHeader } from '@dorsia/web-design-system/src/atoms/SectionHeader';

import { EmailSuggestionButton } from '@/components/EmailSuggestionButton/EmailSuggestionButton.tsx';
import styles from './DetailsPage.module.css';
import eventStyles from '../EventPage/EventPage.module.css';

const DetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const [nexting, setNexting] = useState<boolean>(false);
  const [widget, updateWidget] = useWidgetState();
  const user = useAtomValue(UserAtom);
  const auth = useAtomValue(AuthAtom);
  const restaurant = useAtomValue(RestaurantAtom);
  const allowAltPickup = restaurant?.allow_alternative_pickup === true;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [showAltPickup, setShowAltPickup] = useState<boolean>(
    !!widget.alternative_pickup_first_name
  );
  const redirectedToBookingRef = useRef(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const phoneWasValidRef = useRef(Boolean(widget.phone_number));

  const routeRestaurantId = useRestaurantId();
  // Marketing SMS opt-in is US-only [GRO-7075]. See `resolveWidgetMarketingSmsOptIn`
  // in `../lib/state.ts` for the invariant this checkbox mirrors.
  const isUsPhone = (widget.phone_country ?? 'US') === 'US';
  const marketingSmsOptIn = resolveWidgetMarketingSmsOptIn(widget);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const returnToParam = searchParams.get('returnTo');
    if (returnToParam && returnToParam !== widget.returnTo) {
      updateWidget({ returnTo: returnToParam });
    }
  }, [searchParams, widget.returnTo, updateWidget]);

  useEffect(() => {
    if (redirectedToBookingRef.current || widget.bookingType || widget.returnTo) {
      return;
    }

    const returnToParam = searchParams.get('returnTo');
    if (returnToParam) return;

    redirectedToBookingRef.current = true;
    updateWidget({
      reservation_error: 'Please select your booking before continuing.',
    });
    navigate('../booking', { replace: true });
  }, [widget, updateWidget, navigate, searchParams]);

  const onSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    void onNext();
  };

  const isZeroDollarNoPayment =
    widget.bookingType === 'event' &&
    (widget.option?.price ?? -1) === 0 &&
    !!restaurant?.allow_zero_dollar_booking_links_without_payment_method;

  const isCustomRequest = widget.bookingType === 'custom_request';

  const trackDetailsSubmitted = () => {
    if (routeRestaurantId != null) {
      track('Widget Details Submitted', {
        restaurant_id: routeRestaurantId,
        booking_type: resolveBookingType(widget.bookingType),
      });
    }
  };

  /**
   * `emailOverride` is the address the guest just accepted from the "Did you
   * mean…?" affordance. `updateWidget()` has stored it, but this closure still
   * holds the pre-click `widget`, so the readiness gate below would judge the
   * rejected address — pass the accepted one explicitly rather than race the
   * atom's re-render.
   */
  const onNext = async (emailOverride?: string) => {
    const pendingWidget = emailOverride ? { ...widget, email: emailOverride } : widget;

    // Implicit Terms of Use + Privacy Policy acceptance on Continue [GRO-7075].
    // The DetailsPage now shows "By clicking Continue, you agree to..." beside
    // the button (mirroring the Member app's ApplicationOnboardingInfo screen),
    // so pressing Continue is itself the acceptance. Setting policy_accepted
    // here pre-fills CheckoutPage's Dorsia-terms checkbox so the guest is not
    // asked to agree a second time. Guests who never make it to a click on
    // Continue never get policy_accepted = true.
    //
    // BUT `policy_accepted` is overloaded: for venues with a custom
    // `merchandising_policy_opt_in_label`, CheckoutPage binds that same flag
    // to a checkbox showing the venue's bespoke merchandising-policy legal
    // language (not the generic Dorsia Terms/Privacy line shown here).
    // Implicitly accepting it would silently pre-check venue-specific legal
    // consent the guest never saw, so scope the implicit acceptance to venues
    // WITHOUT a custom label — those keep an explicit opt-in on CheckoutPage.
    const acceptsPolicyImplicitly =
      widget.policy_accepted !== true && !restaurant?.merchandising_policy_opt_in_label;

    // Custom requests always require fresh phone verification
    // For other flows, skip to payment if already authenticated
    if (!isCustomRequest && auth?.token && (user.phone || widget.phone_confirmed)) {
      // Fire only on a path that actually advances the funnel, so dead clicks
      // / unfilled forms don't inflate the "Details Submitted" count.
      trackDetailsSubmitted();
      if (acceptsPolicyImplicitly) {
        updateWidget({ policy_accepted: true });
      }
      // Older / returning users skip `/verify` entirely, so the opt-in flag
      // piggybacked on `POST /v2.0/verification` never fires for them. Call
      // the authenticated opt-in endpoint directly here so the checkbox
      // works for guests who already hold a token [GRO-7075]. Fire-and-forget
      // so Braze failures cannot block a booking. Server enforces the
      // US-only gate again via GeneralMarketingSms::isForUser().
      if (resolveWidgetMarketingSmsOptIn(widget)) {
        subscribeToGeneralMarketingSms(auth.token).catch((e: unknown) => {
          Sentry.captureException(e, { tags: { source: 'widget_details_marketing_sms_optin' } });
        });
      }
      // Back the "By clicking Continue, you agree to Terms of Use and Privacy
      // Policy" copy shown above the button so the Member app's terms gate
      // does not later block a widget booker on first launch. The endpoint is
      // idempotent (strictly append-once, see UserController::acceptTerms),
      // so calling it on every booking will not overwrite an existing
      // acceptance timestamp. Fire-and-forget for the same reason as the
      // marketing SMS call: a Braze/DB blip must never block checkout.
      acceptTerms(auth.token).catch((e: unknown) => {
        Sentry.captureException(e, { tags: { source: 'widget_details_accept_terms' } });
      });
      navigate(isZeroDollarNoPayment ? '../checkout' : '../payment');
      return;
    }

    // Otherwise, send verification SMS and go to verify page
    if (isEventInfoReady(pendingWidget)) {
      trackDetailsSubmitted();
      try {
        setNexting(true);
        updateWidget({
          payment_method: undefined,
          ...(acceptsPolicyImplicitly ? { policy_accepted: true } : {}),
        });
        const recaptchaToken = await recaptchaTokenFor('send_code', executeRecaptcha);
        await verify(widget.phone_number!, undefined, recaptchaToken);
        if (routeRestaurantId != null) {
          track('Widget Verify Started', {
            restaurant_id: routeRestaurantId,
            booking_type: resolveBookingType(widget.bookingType),
          });
        }
        setNexting(false);
        navigate('../verify');
      } catch (e: unknown) {
        setNexting(false);
        Sentry.captureException(e);
        updateWidget({
          update_user_error: 'Failed to send verification code. Please try again.',
        });
      }
    }
  };

  const onFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateWidget({ first_name: event.currentTarget.value });
  };

  const onLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateWidget({ last_name: event.currentTarget.value });
  };

  const [localPhone, setLocalPhone] = useState(() => {
    const num = widget.phone_number;
    if (!num) return '';
    try {
      const parsed = parsePhoneNumber(num);
      return parsed?.nationalNumber ?? '';
    } catch {
      return '';
    }
  });

  /**
   * Parse national input via `getValidPhone` (trunk-0 safe + isValid fail-closed)
   * and emit a PII-free rejection event when a previously-valid E.164 is lost.
   */
  const resolvePhoneNumber = (
    national: string,
    country: Country,
    reason: WidgetPhoneRejectReason
  ): string | undefined => {
    const e164 = getValidPhone(national.trim() || undefined, country)?.number;
    if (phoneWasValidRef.current && !e164 && national.trim() !== '' && routeRestaurantId != null) {
      track('Widget Phone Rejected', {
        restaurant_id: routeRestaurantId,
        booking_type: resolveBookingType(widget.bookingType),
        phone_country: country,
        reason,
      });
    }
    phoneWasValidRef.current = Boolean(e164);
    return e164;
  };

  const onPhoneCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const country = event.target.value as Country;
    if (localPhone.trim() !== '') {
      setPhoneTouched(true);
    }
    updateWidget({
      phone_country: country,
      // Parse via libphonenumber so a national trunk `0` (FR/UK/…) is not
      // glued onto the country calling code (HOS-2391: +3307… vs +337…).
      phone_number: resolvePhoneNumber(localPhone, country, 'country_mismatch'),
    });
  };

  const onPhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLocalPhone(value);
    const country = widget.phone_country ?? 'US';
    updateWidget({
      phone_number: resolvePhoneNumber(value, country, 'invalid'),
    });
  };

  const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Typing their own correction retires the suggestion AND the message: both
    // were computed for the address that was rejected, and keeping either against
    // a different one would either put back a typo the guest just removed or
    // condemn a domain nobody has judged yet.
    updateWidget({
      email: event.currentTarget.value,
      email_error: undefined,
      email_domain_suggestion: undefined,
    });
  };

  /**
   * Accepting the "Did you mean…?" correction. This click is the ONLY thing
   * allowed to change the email on the guest's behalf [GRO-6429] — the
   * suggestion is never applied automatically.
   *
   * Resubmitting means exactly what pressing "Continue" means here: send a
   * fresh verification code and return to `../verify`, where `updateUser` runs
   * again with the corrected address. That is the same recovery a guest fixing
   * the typo by hand would go through, so this is a shortcut through the
   * existing path rather than a second one.
   */
  const onAcceptEmailSuggestion = () => {
    const suggestion = widget.email_domain_suggestion;
    if (!suggestion) return;

    const suggestedEmail = suggestion.suggested_email;
    updateWidget({
      email: suggestedEmail,
      email_domain_suggestion: undefined,
      email_error: undefined,
      update_user_error: undefined,
    });
    void onNext(suggestedEmail);
  };

  const onAltFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateWidget({ alternative_pickup_first_name: event.currentTarget.value });
  };

  const onAltLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateWidget({ alternative_pickup_last_name: event.currentTarget.value });
  };

  const onAltPhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateWidget({ alternative_pickup_phone: event.currentTarget.value });
  };

  const onAltEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateWidget({ alternative_pickup_email: event.currentTarget.value });
  };

  const onRemoveAltPickup = () => {
    updateWidget({
      alternative_pickup_first_name: undefined,
      alternative_pickup_last_name: undefined,
      alternative_pickup_phone: undefined,
      alternative_pickup_email: undefined,
    });
    setShowAltPickup(false);
  };

  return (
    <div className={eventStyles.eventContent}>
      <div className={eventStyles.card}>
        {widget?.update_user_error && (
          <div className={styles.headerError}>There was an issue with your details.</div>
        )}

        <div className={styles.restaurantTitle}>
          <Suspense fallback={<RestaurantNameLoader />}>
            <RestaurantName />
          </Suspense>
          {restaurant?.type === 'event' && <Badge variant="event">EVENT</Badge>}
        </div>

        <form onSubmit={onSubmit}>
          <SectionHeader title="Details" />
          <p className={styles.sectionSubtitle}>Please enter your details for the booking.</p>

          {widget?.update_user_error && (
            <div className={styles.error}>{widget?.update_user_error}</div>
          )}

          <div className={styles.nameRow}>
            <InputField
              label="First name"
              placeholder="First name"
              value={widget.first_name ?? ''}
              onChange={onFirstNameChange}
              autoFocus={widget.first_name === undefined}
            />
            <InputField
              label="Last name"
              placeholder="Last name"
              value={widget.last_name ?? ''}
              onChange={onLastNameChange}
            />
          </div>

          <div className={styles.fieldGroup}>
            {widget.phone_confirmed ? (
              <InputField
                label="Phone number (confirmed)"
                value={widget.phone_number ?? ''}
                readOnly
                disabled
              />
            ) : (
              <div className={styles.phoneFieldWrapper}>
                <label htmlFor="dorsia-phone-number">Phone number</label>
                <div className={styles.phoneRow}>
                  <select
                    className={styles.countrySelect}
                    value={widget.phone_country ?? 'US'}
                    onChange={onPhoneCountryChange}
                    aria-label="Country calling code"
                  >
                    {getCountries().map((c) => (
                      <option key={c} value={c}>
                        {c} +{getCountryCallingCode(c)}
                      </option>
                    ))}
                  </select>
                  <input
                    id="dorsia-phone-number"
                    className={styles.phoneInput}
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={localPhone}
                    onChange={onPhoneNumberChange}
                    onBlur={() => setPhoneTouched(true)}
                    aria-invalid={
                      phoneTouched &&
                      localPhone.trim() !== '' &&
                      !getValidPhone(localPhone, widget.phone_country ?? 'US')
                    }
                  />
                </div>
                {phoneTouched &&
                  localPhone.trim() !== '' &&
                  !getValidPhone(localPhone, widget.phone_country ?? 'US') && (
                    <span className={styles.fieldError}>Enter a valid phone number.</span>
                  )}
              </div>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <InputField
              label="Email"
              type="email"
              placeholder="Enter email address"
              value={widget.email ?? ''}
              onChange={onEmailChange}
              onBlur={() => setEmailTouched(true)}
              // The server's rejection renders in this field's own slot, directly
              // above the suggestion that corrects it. It used to print at the top
              // of the form, describing an input three rows below it.
              //
              // Local format feedback wins while it applies: it describes what the
              // guest is typing now, while the server error describes the address
              // they submitted. Both are rarely live at once — editing the email
              // clears the server one.
              error={
                emailTouched && widget.email && !isValidEmail(widget.email)
                  ? 'Please enter a valid email address'
                  : widget.email_error
              }
            />

            {widget.email_domain_suggestion && (
              <EmailSuggestionButton
                suggestedEmail={widget.email_domain_suggestion.suggested_email}
                onAccept={onAcceptEmailSuggestion}
                className={styles.suggestionPlacement}
              />
            )}
          </div>

          {isUsPhone && (
            <div className={styles.smsOptIn}>
              <Checkbox
                checked={marketingSmsOptIn}
                onChange={(checked) => updateWidget({ marketing_sms_opt_in: checked })}
                // Copy hardcoded to match the Member app's
                // ApplicationOnboardingInfo screen [GRO-7075]. The API's
                // `subscription_groups.marketing.sms.title` string is stale
                // ("Special stuff via SMS") and the marketing-approved label
                // + TCPA-compliant fine print are not yet plumbed through
                // `getAvailableSubscriptionGroups`. When the API is updated,
                // both surfaces (widget + app) should read from `option.title`
                // / `option.description` again so these two strings stay in
                // lockstep. Fine print is TCPA-required for US SMS opt-ins.
                label="Dorsia credit drops and new restaurant news—all via text."
              />
              <p className={styles.smsOptInFinePrint}>
                Opt-in to marketing messages from Dorsia. Message and data rates may apply. Message
                frequency varies. Reply STOP to cancel or HELP for support.{' '}
                <a
                  href="https://www.dorsia.com/terms-of-use"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Dorsia SMS Terms of Service
                </a>
                .
              </p>
            </div>
          )}

          {allowAltPickup &&
            (!showAltPickup ? (
              <button
                type="button"
                className={styles.altPickupToggle}
                onClick={() => setShowAltPickup(true)}
              >
                Add an alternative pickup contact (optional)
              </button>
            ) : (
              <div className={styles.altPickupSection}>
                <div className={styles.altPickupHeader}>
                  <h4 className={styles.altPickupTitle}>Alternative Pickup Contact</h4>
                  <button type="button" className={styles.removeLink} onClick={onRemoveAltPickup}>
                    REMOVE
                  </button>
                </div>
                <p className={styles.altPickupSubtitle}>
                  Designate someone else to receive tickets at will-call.
                </p>

                <div className={styles.nameRow}>
                  <InputField
                    label="First name"
                    placeholder="First name"
                    value={widget.alternative_pickup_first_name ?? ''}
                    onChange={onAltFirstNameChange}
                  />
                  <InputField
                    label="Last name"
                    placeholder="Last name"
                    value={widget.alternative_pickup_last_name ?? ''}
                    onChange={onAltLastNameChange}
                  />
                </div>

                <div className={styles.fieldGroup}>
                  <InputField
                    label="Phone"
                    type="tel"
                    placeholder="Enter phone number"
                    value={widget.alternative_pickup_phone ?? ''}
                    onChange={onAltPhoneChange}
                  />
                </div>

                <div className={styles.fieldGroup}>
                  <InputField
                    label="Email"
                    type="email"
                    placeholder="Enter email address"
                    value={widget.alternative_pickup_email ?? ''}
                    onChange={onAltEmailChange}
                  />
                </div>
              </div>
            ))}

          <p className={styles.termsAgreement}>
            By clicking Continue, you agree to our{' '}
            <a href="https://www.dorsia.com/terms-of-use" target="_blank" rel="noopener noreferrer">
              Terms of Use
            </a>{' '}
            and{' '}
            <a href="https://www.dorsia.com/privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            .
          </p>

          <Button
            variant="primary"
            fullWidth
            loading={nexting}
            disabled={!isEventInfoReady(widget)}
            onClick={() => void onNext()}
          >
            Continue
          </Button>

          <Link to="../" className={styles.backLink}>
            Back
          </Link>
        </form>
      </div>
    </div>
  );
};

export default DetailsPage;

